import { useRef, useState } from 'react'
import { type Dispatch, type SetStateAction } from 'react'
import { type TFunction } from 'i18next'
import LogTool from '../../../logger/logTools'

const log = new LogTool({ context: 'useManualSharesHandler', enable: true, logLevel: 'warn' })

type ManualShareFieldObject = {
  key: string
  type?: 'existingManualShare' | 'newManualShare'
  label: string
  onChange: (input: string) => void
}

type FormState = {
  state: any
}

/**
 * Custom hook that handles all manual shares related logic for Sharepoint Drawers.
 * This hook manages:
 * - Manual shares form state
 * - Manual shares form input state
 * - Unique key generation for manual share fields
 * - Generating manual share field objects
 * - Adding and deleting manual shares
 * - Validating manual share comments
 *
 * @param t - Translation function
 * @param log - LogTool instance for logging
 * @param formState - Form state object (used to get password from state)
 * @param securePasswordGenerator - Secure password generator instance
 * @param isEditMode - Whether the drawer is in edit mode (affects field object type)
 * @returns Object containing manualSharesForm state, manualSharesFormInput state, and utility functions
 */
export function useManualSharesHandler(
  t: TFunction,
  log: LogTool,
  formState: FormState,
  securePasswordGenerator: any,
  isEditMode = false
): {
  manualSharesForm: any[]
  setManualSharesForm: Dispatch<SetStateAction<any[]>>
  manualSharesFormInput: { [key: string]: { comment: string; password?: string } }
  setManualSharesFormInput: Dispatch<SetStateAction<{ [key: string]: { comment: string; password?: string } }>>
  generateManualShareFieldObject: (type?: 'existingManualShare' | 'newManualShare') => ManualShareFieldObject
  handleAddManualShare: () => void
  handleDeleteManualShare: (fieldObj: any) => void
  missingManualShareComment: () => boolean
  resetManualShares: () => void
} {
  const uniqueKeyStore = useRef(0)
  const [manualSharesForm, setManualSharesForm] = useState<any[]>([{ type: 'addManualShare' }])
  const [manualSharesFormInput, setManualSharesFormInput] = useState<{
    [key: string]: { comment: string; password?: string }
  }>({})

  /**
   * Generates a unique element key (ascending number).
   * @returns
   */
  function getUniqueKey(): string {
    const key = uniqueKeyStore.current
    uniqueKeyStore.current += 1
    return String(key)
  }

  /**
   * Creates a new, unique ManualShareFieldObject that can be inserted into the manualSharesForm.
   * @param type - Type of manual share field ('existingManualShare' for edit mode, 'newManualShare' for create mode)
   * @returns
   */
  function generateManualShareFieldObject(
    type: 'existingManualShare' | 'newManualShare' = isEditMode ? 'existingManualShare' : 'newManualShare'
  ): ManualShareFieldObject {
    const fieldObjKey = `manualShare${getUniqueKey()}`
    return {
      key: fieldObjKey,
      type: type,
      label: t('common:content.label.comment'),
      onChange: (input: string) => {
        log.debug('Updating', fieldObjKey, 'with value ->', input)
        setManualSharesFormInput((prev: any) => {
          if (prev[fieldObjKey]?.comment) {
            // the user entered a comment before already -> update it
            prev[fieldObjKey] = { ...prev[fieldObjKey], comment: input }
          } else {
            prev[fieldObjKey] = {
              comment: input,
              password: formState.state?.password ?? securePasswordGenerator.randomPassword(),
            }
          }
          return { ...prev }
        })
      },
    }
  }

  /**
   * Adds a new manual share field to the form.
   */
  const handleAddManualShare = () => {
    setManualSharesForm((prev: any) => {
      // we need to store this index to enable the onChange method
      const fieldObjIndex: number = prev.length - 1

      // replace the last element with a new textfield
      prev[fieldObjIndex] = generateManualShareFieldObject('newManualShare')

      // add 'addFormField' entry to the end of formJson
      return [...prev, { type: 'addManualShare' }]
    })
  }

  /**
   * Deletes a manual share field from the form.
   * @param manualShareFieldObj - The manual share field object to delete
   */
  const handleDeleteManualShare = (manualShareFieldObj: any) => {
    setManualSharesForm((prev: any) => {
      const i = prev.indexOf(manualShareFieldObj)
      // remove manualShareFieldObject
      prev.splice(i, 1)

      // delete the according manualShareFormInput
      setManualSharesFormInput((prev: any) => {
        const copy = { ...prev }
        // Type guard: only delete when key exists
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete copy[manualShareFieldObj.key]
        return copy
      })
      return [...prev]
    })
  }

  /**
   * Determines if there are any empty manual share comment input fields.
   * @returns
   */
  function missingManualShareComment(): boolean {
    let commentIsMissing = false
    const manualSharesFormInputValues: { comment: string; password?: string }[] =
      Object.values(manualSharesFormInput)
    if (manualSharesForm.length - 1 > manualSharesFormInputValues.length) {
      // there is a manualSharesFormFieldObject that was not yet used to enter a manual share comment
      commentIsMissing = true
    }
    if (manualSharesFormInputValues.length > 0) {
      for (const manualShare of manualSharesFormInputValues) {
        if (manualShare.comment === '') {
          commentIsMissing = true
          break
        }
      }
    }
    return commentIsMissing
  }

  /**
   * Resets manual shares to initial state (used in handleClose).
   */
  const resetManualShares = () => {
    uniqueKeyStore.current = 0
    setManualSharesForm([{ type: 'addManualShare' }])
    setManualSharesFormInput({})
  }

  return {
    manualSharesForm,
    setManualSharesForm,
    manualSharesFormInput,
    setManualSharesFormInput,
    generateManualShareFieldObject,
    handleAddManualShare,
    handleDeleteManualShare,
    missingManualShareComment,
    resetManualShares,
  }
}
